interface PeakMeterOptions {
  borderSize: number;
  fontSize: number;
  backgroundColor: string;
  tickColor: string;
  labelColor: string;
  gradient: string[];
  dbTickSize: number;
  maskTransition: string;
  audioMeterStandard: 'peak-sample' | 'true-peak' | 'peak-sample';
  minDecibels: number;
  maxDecibels: number;
}

class AudioPeakMeter {
  private static mediaElementNodeMap = new WeakMap<HTMLVideoElement, MediaElementAudioSourceNode>();

  private options: PeakMeterOptions = {
    borderSize: 2,
    fontSize: 9,
    backgroundColor: 'black',
    tickColor: '#ddd',
    labelColor: '#ddd',
    gradient: ['red 1%', '#ff0 16%', 'lime 45%', '#080 100%'],
    dbTickSize: 6,
    maskTransition: '0.1s',
    audioMeterStandard: 'peak-sample', // Could be "true-peak" (ITU-R BS.1770) or "peak-sample"
    minDecibels: -60,
    maxDecibels: 0,
  };
  private tickWidth: number;
  private elementWidth: number;
  private elementHeight: number;
  private meterHeight: number;
  private meterWidth: number;
  private meterTop: number;
  private vertical = true;
  private channelCount = 2;
  private channelMasks: HTMLDivElement[] = [];
  private channelPeaks: number[] = [];
  private channelPeakLabels: HTMLDivElement[] = [];
  private maskSizes: number[] = [];
  private textLabels: string[] = [];
  private refreshEveryApproxMs = 20;
  private dataArrays: Uint8Array[];
  private muted: boolean = true;

  public id: string | null = null;

  // Used for ITU-R BS.1770
  private lpfCoefficients: number[] = [];
  private lpfBuffer: number[] = [];
  private upsampleFactor = 4;
  private lastChannelTP: number[] = [];
  private decayFactor = 0.99999;

  private splitterNode: ChannelSplitterNode;
  private leftAnalyserNode: AnalyserNode;
  private rightAnalyserNode: AnalyserNode;
  private gainNode: GainNode;
  private sourceNode: MediaElementAudioSourceNode;

  private static getSourceNodeAndCtx = (videoEl: HTMLVideoElement) => {
    let sourceNode = AudioPeakMeter.mediaElementNodeMap.get(videoEl);

    if (!sourceNode) {
      const audioCtx: AudioContext = new AudioContext();
      sourceNode = audioCtx.createMediaElementSource(videoEl);
      AudioPeakMeter.mediaElementNodeMap.set(videoEl, sourceNode);
    }

    return {
      sourceNode,
      audioCtx: sourceNode.context,
    };
  };

  constructor(
    domElement: HTMLElement,
    videoEl: HTMLVideoElement,
    userOptions: Partial<PeakMeterOptions>,
    id: string,
    muted: boolean
  ) {
    this.options = {
      ...this.options,
      ...userOptions,
    };
    this.id = id;
    this.tickWidth = this.options.fontSize * 2.0;
    this.meterTop = this.options.fontSize * 1.5 + this.options.borderSize;
    this.muted = muted;
    const { sourceNode, audioCtx } = AudioPeakMeter.getSourceNodeAndCtx(videoEl);
    this.sourceNode = sourceNode;

    // videoEl.addEventListener('play', () => {
    //   AudioPeakMeter.audioCtx.resume();
    // });

    const splitter = audioCtx.createChannelSplitter(2);
    this.splitterNode = splitter;
    this.sourceNode.connect(splitter);

    const leftAnalyserNode = audioCtx.createAnalyser();
    leftAnalyserNode.minDecibels = this.options.minDecibels;
    leftAnalyserNode.maxDecibels = this.options.maxDecibels;
    leftAnalyserNode.smoothingTimeConstant = 0.3;
    leftAnalyserNode.fftSize = 512;
    this.leftAnalyserNode = leftAnalyserNode;
    splitter.connect(leftAnalyserNode, 0, 0);

    const rightAnalyserNode = audioCtx.createAnalyser();
    rightAnalyserNode.minDecibels = this.options.minDecibels;
    rightAnalyserNode.maxDecibels = this.options.maxDecibels;
    rightAnalyserNode.smoothingTimeConstant = 0.3;
    rightAnalyserNode.fftSize = 512;
    this.rightAnalyserNode = rightAnalyserNode;
    splitter.connect(rightAnalyserNode, 1, 0);

    const mergerNode = audioCtx.createChannelMerger(2);
    this.leftAnalyserNode.connect(mergerNode, 0, 0);
    this.rightAnalyserNode.connect(mergerNode, 0, 1);
    const gainNode = audioCtx.createGain();
    this.gainNode = gainNode;
    mergerNode.connect(gainNode);
    gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
    gainNode.connect(audioCtx.destination);

    this.dataArrays = [
      new Uint8Array(leftAnalyserNode.frequencyBinCount),
      new Uint8Array(rightAnalyserNode.frequencyBinCount),
    ];

    this.elementWidth = domElement.clientWidth;
    this.elementHeight = domElement.clientHeight;
    var meterElement = this.createContainerDiv(domElement);
    if (this.elementWidth > this.elementHeight) {
      this.vertical = false;
    }
    this.meterHeight = this.elementHeight - this.meterTop - this.options.borderSize;
    this.meterWidth = this.elementWidth - this.tickWidth - this.options.borderSize;
    this.createTicks(meterElement);
    this.createRainbow(
      meterElement,
      this.meterWidth,
      this.meterHeight,
      this.meterTop,
      this.tickWidth
    );
    var channelWidth = this.meterWidth / this.channelCount;

    if (!this.vertical) {
      channelWidth = this.meterHeight / this.channelCount;
    }

    let channelLeft = this.tickWidth;
    if (!this.vertical) {
      channelLeft = this.meterTop;
    }

    for (var i = 0; i < this.channelCount; i++) {
      this.createChannelMask(
        meterElement,
        this.options.borderSize,
        this.meterTop,
        channelLeft,
        false
      );
      this.channelMasks[i] = this.createChannelMask(
        meterElement,
        channelWidth,
        this.meterTop,
        channelLeft,
        this.options.maskTransition
      );
      this.channelPeaks[i] = 0.0;
      this.channelPeakLabels[i] = this.createPeakLabel(meterElement, channelWidth, channelLeft);
      channelLeft += channelWidth;
      this.maskSizes[i] = 0;
      this.textLabels[i] = '-∞';
    }
    // this.meterNode.onaudioprocess = this.updateMeter;
    meterElement.addEventListener(
      'click',
      () => {
        for (var i = 0; i < this.channelCount; i++) {
          this.channelPeaks[i] = 0.0;
          this.textLabels[i] = '-∞';
        }
      },
      false
    );
    this.paintMeter();
  }

  private getBaseLog = (x: number, y: number) => {
    return Math.log(y) / Math.log(x);
  };

  private dbFromFloat = (floatVal: number) => {
    return this.getBaseLog(10, floatVal) * 20;
  };

  private findAudioProcBufferSize = (numSamplesIn: number) => {
    return [256, 512, 1024, 2048, 4096, 8192, 16384].reduce((a, b) => {
      return Math.abs(b - numSamplesIn) < Math.abs(a - numSamplesIn) ? b : a;
    });
  };

  private createContainerDiv = (parent: HTMLElement) => {
    var meterElement = document.createElement('div');
    meterElement.style.position = 'relative';
    meterElement.style.width = this.elementWidth + 'px';
    meterElement.style.height = this.elementHeight + 'px';
    meterElement.style.backgroundColor = this.options.backgroundColor;
    parent.appendChild(meterElement);
    return meterElement;
  };

  private createTicks = (parent: HTMLElement) => {
    const dbRange = Math.abs(this.options.maxDecibels - this.options.minDecibels);
    var numTicks = Math.floor(dbRange / this.options.dbTickSize);
    var dbTickLabel = 0;
    if (this.vertical) {
      var dbTickTop = this.options.fontSize + this.options.borderSize;
      for (var i = 0; i < numTicks; i++) {
        var dbTick = document.createElement('div');
        parent.appendChild(dbTick);
        dbTick.style.width = this.tickWidth + 'px';
        dbTick.style.textAlign = 'right';
        dbTick.style.color = this.options.tickColor;
        dbTick.style.fontSize = this.options.fontSize + 'px';
        dbTick.style.position = 'absolute';
        dbTick.style.top = dbTickTop + 'px';
        dbTick.textContent = dbTickLabel + '';
        dbTickLabel -= this.options.dbTickSize;
        dbTickTop += this.meterHeight / numTicks;
      }
    } else {
      this.tickWidth = this.meterWidth / numTicks;
      var dbTickRight = this.options.fontSize * 2;
      for (var j = 0; j < numTicks; j++) {
        var dbTickElse = document.createElement('div');
        parent.appendChild(dbTickElse);
        dbTickElse.style.width = this.tickWidth + 'px';
        dbTickElse.style.textAlign = 'right';
        dbTickElse.style.color = this.options.tickColor;
        dbTickElse.style.fontSize = this.options.fontSize + 'px';
        dbTickElse.style.position = 'absolute';
        dbTickElse.style.right = dbTickRight + 'px';
        dbTickElse.textContent = dbTickLabel + '';
        dbTickLabel -= this.options.dbTickSize;
        dbTickRight += this.tickWidth;
      }
    }
  };

  private createRainbow = (
    parent: HTMLElement,
    width: number,
    height: number,
    top: number,
    left: number
  ) => {
    var rainbow = document.createElement('div');
    parent.appendChild(rainbow);
    rainbow.style.width = width + 'px';
    rainbow.style.height = height + 'px';
    rainbow.style.position = 'absolute';
    rainbow.style.top = top + 'px';
    var gradientStyle = 'linear-gradient(to bottom, ' + this.options.gradient.join(', ') + ')';
    if (this.vertical) {
      rainbow.style.left = left + 'px';
      gradientStyle = 'linear-gradient(to bottom, ' + this.options.gradient.join(', ') + ')';
    } else {
      rainbow.style.left = this.options.borderSize + 'px';
      gradientStyle = 'linear-gradient(to left, ' + this.options.gradient.join(', ') + ')';
    }
    rainbow.style.backgroundImage = gradientStyle;
    return rainbow;
  };

  private createPeakLabel = (parent: HTMLDivElement, width: number, left: number) => {
    var label = document.createElement('div');
    parent.appendChild(label);
    label.style.textAlign = 'center';
    label.style.color = this.options.labelColor;
    label.style.fontSize = this.options.fontSize + 'px';
    label.style.position = 'absolute';
    label.textContent = '-∞';
    if (this.vertical) {
      label.style.width = width + 'px';
      label.style.top = this.options.borderSize + 'px';
      label.style.left = left + 'px';
    } else {
      label.style.width = this.options.fontSize * 2 + 'px';
      label.style.right = this.options.borderSize + 'px';
      label.style.top = width * 0.25 + left + 'px';
    }
    return label;
  };

  private createChannelMask = (
    parent: HTMLElement,
    width: number,
    top: number,
    left: number,
    transition: string | boolean
  ) => {
    const channelMask = document.createElement('div');
    parent.appendChild(channelMask);
    channelMask.style.position = 'absolute';
    if (this.vertical) {
      channelMask.style.width = width + 'px';
      channelMask.style.height = this.meterHeight + 'px';
      channelMask.style.top = top + 'px';
      channelMask.style.left = left + 'px';
    } else {
      channelMask.style.width = this.meterWidth + 'px';
      channelMask.style.height = width + 'px';
      channelMask.style.top = left + 'px';
      channelMask.style.right = this.options.fontSize * 2 + 'px';
    }
    channelMask.style.backgroundColor = this.options.backgroundColor;
    if (transition) {
      if (this.vertical) {
        channelMask.style.transition = 'height ' + this.options.maskTransition;
      } else {
        channelMask.style.transition = 'width ' + this.options.maskTransition;
      }
    }
    return channelMask;
  };

  private maskSize = (floatVal: number) => {
    var meterDimension = this.vertical ? this.meterHeight : this.meterWidth;
    if (!floatVal) {
      return meterDimension;
    } else {
      var d = 128;
      var returnVal = Math.floor((1 - floatVal / d) * meterDimension);
      if (returnVal > meterDimension) {
        return meterDimension;
      } else {
        return returnVal;
      }
    }
  };

  private updateMeter = () => {
    this.leftAnalyserNode.getByteFrequencyData(this.dataArrays[0]);
    this.rightAnalyserNode.getByteFrequencyData(this.dataArrays[1]);
    var channelMaxes = [];

    // Calculate peak levels
    // if (this.options.audioMeterStandard === 'true-peak') {
    //   // This follows ITU-R BS.1770 (True Peak meter)
    //   channelMaxes = this.calculateTPValues(inputBuffer);
    // }
    // else {
    // Just get the peak level
    channelMaxes = this.calculateMaxValues();
    // }

    // Update peak & text values
    for (var i = 0; i < this.channelCount; i++) {
      this.maskSizes[i] = this.maskSize(channelMaxes[i]);
      if (channelMaxes[i] > this.channelPeaks[i]) {
        this.channelPeaks[i] = channelMaxes[i];
        this.textLabels[i] = this.dbFromFloat(this.channelPeaks[i]).toFixed(1);
      }
    }
  };

  private calculateMaxValues = () => {
    var channelMaxes = [];

    for (var c = 0; c < 2; c++) {
      channelMaxes[c] = 0.0;
      var channelData = this.dataArrays[c];
      for (var s = 0; s < channelData.length; s++) {
        if (Math.abs(channelData[s]) > channelMaxes[c]) {
          channelMaxes[c] = Math.abs(channelData[s]);
        }
      }
    }
    return channelMaxes;
  };

  private calculateTPValues = (inputBuffer: AudioBuffer) => {
    var channelCount = inputBuffer.numberOfChannels;
    // Ini TP values
    if (this.lastChannelTP.length <= 0) {
      console.log('Initialing TP values for ' + channelCount + 'channels');
      this.lastChannelTP = this.createAndIniArray(channelCount, 0.0);
      // Decay time ms = 1700 and -20Db
      var attFactor = Math.pow(10.0, -20 / 10.0);
      var decayTimeS = 1700 / 1000;
      this.decayFactor = Math.pow(attFactor, 1.0 / (inputBuffer.sampleRate * decayTimeS));
      console.log('Initialized with decayFactor ' + this.decayFactor);
    }
    for (var c = 0; c < channelCount; c++) {
      var channelData = inputBuffer.getChannelData(c);
      // Process according to ITU-R BS.1770
      var overSampledAndLPF = this.audioOverSampleAndFilter(channelData, inputBuffer.sampleRate);
      for (var s = 0; s < overSampledAndLPF.length; s++) {
        this.lastChannelTP[c] = this.lastChannelTP[c] * this.decayFactor;
        if (Math.abs(overSampledAndLPF[s]) > this.lastChannelTP[c]) {
          this.lastChannelTP[c] = Math.abs(overSampledAndLPF[s]);
        }
      }
    }
    return this.lastChannelTP;
  };

  private audioOverSampleAndFilter = (channelData: Float32Array, inputFs: number) => {
    let res: number[] = [];
    // Initialize filter coefficients and buffer
    if (this.lpfCoefficients.length <= 0) {
      console.log('Initialing filter components for ITU-R BS.1770, fs: ' + inputFs);
      if (inputFs >= 96000) {
        this.upsampleFactor = 2;
      }
      this.lpfCoefficients = this.calculateLPFCoefficients(33);
      this.lpfBuffer = this.createAndIniArray(this.lpfCoefficients.length, 0.0);
      console.log(
        'Initialized lpfCoefficients lpfCoefficients=[' +
          this.lpfCoefficients.join(',') +
          '], and lpfBuffer: [' +
          this.lpfBuffer.join(',') +
          ']'
      );
    }
    for (var ni = 0; ni < channelData.length; ni++) {
      var samplesOut = this.filterSample(channelData[ni]); // 1 input sample -> generated upsampleFactor samples
      res = res.concat(samplesOut);
    }
    return res;
  };

  private calculateLPFCoefficients = (numCoefficients: number) => {
    var retCoefs = [];
    var fcRel = 1.0 / (4.0 * this.upsampleFactor);
    var coefsLim = Math.floor((numCoefficients - 1) / 2);
    for (var n = -coefsLim; n <= coefsLim; n++) {
      var wn = 0.54 + 0.46 * Math.cos((2.0 * Math.PI * n) / numCoefficients);
      var hn = 0.0;
      if (n === 0) {
        hn = 2.0 * fcRel;
      } else {
        hn = Math.sin(2.0 * Math.PI * fcRel * n) / (Math.PI * n);
      }
      //Adapt windows & upsampler factor
      hn = wn * hn * this.upsampleFactor;
      retCoefs.push(hn);
    }
    return retCoefs;
  };

  private createAndIniArray = (numElements: number, iniVal: number) => {
    var ret = [];
    for (var n = 0; n < numElements; n++) {
      ret[n] = iniVal;
    }
    return ret;
  };

  private filterSample = (sample: number) => {
    var ret = [];
    this.lpfBuffer.push(sample);
    if (this.lpfBuffer.length >= this.lpfCoefficients.length) {
      this.lpfBuffer.shift();
    }

    for (var nA = 0; nA < this.upsampleFactor; nA++) {
      var nT = 0;
      var retVal = 0;
      for (var nc = nA; nc < this.lpfCoefficients.length; nc = nc + this.upsampleFactor) {
        retVal = retVal + this.lpfCoefficients[nc] * this.lpfBuffer[this.lpfBuffer.length - 1 - nT];
        nT++;
      }
      ret.push(retVal);
    }
    return ret;
  };

  private paintMeter = () => {
    this.updateMeter();
    for (var i = 0; i < this.channelCount; i++) {
      if (this.vertical) {
        this.channelMasks[i].style.height = this.maskSizes[i] + 'px';
      } else {
        this.channelMasks[i].style.width = this.maskSizes[i] + 'px';
      }
      this.channelPeakLabels[i].textContent = this.textLabels[i];
    }
    requestAnimationFrame(this.paintMeter);
  };

  setMuted = (muted: boolean) => {
    this.muted = muted;
    this.gainNode.gain.setValueAtTime(muted ? 0 : 1, this.sourceNode.context.currentTime);
  };

  destroy = () => {
    this.leftAnalyserNode.disconnect();
    this.rightAnalyserNode.disconnect();
    this.splitterNode.disconnect();
    this.sourceNode.disconnect(this.splitterNode);
  };
}

export default AudioPeakMeter;
